import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, isNotEmptyArray, isNotNil, Nillable } from '@web/utils';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { AdminProductDetails } from '../../models/admin-product-details';
import { AdminProductDataSourceService } from '../../services/admin-product-data-source.service';
import {
  BooleanPipe,
  CurrencyComponent,
  DateComponent,
  DetailsViewComponent,
  NoDataComponent,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import {
  AdminMultimediaDataSourceService,
  MultimediaGalleriaThumbnailsComponent,
} from '@web/shared/module/multimedia';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import { AdminProductCharacteristicDetails } from '../../models/admin-product-characteristic-details';
import { AdminProductAttributeDetails } from '../../models/admin-product-attribute-details';
import { SummernoteHtmlPreviewComponent } from '@web/shared/external';
import { AdminProductStatusComponent } from '../../components/admin-product-status/admin-product-status.component';
import { AdminProductLinkComponent } from '../../components/admin-product-link/admin-product-link.component';

@Component({
  selector: 'app-admin-product-details',
  imports: [
    CommonModule,
    SpinnerFilledComponent,
    CurrencyComponent,
    DetailsViewComponent,
    NoDataComponent,
    DateComponent,
    MultimediaGalleriaThumbnailsComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    SummernoteHtmlPreviewComponent,
    AdminProductStatusComponent,
    BooleanPipe,
    AdminProductLinkComponent,
  ],
  templateUrl: './admin-product-details.component.html',
  styleUrl: './admin-product-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminProductDetailsComponent {
  private readonly productDataSource = inject(AdminProductDataSourceService);
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);

  public readonly id = input.required<Guid>();

  protected readonly id$ = toObservable(this.id);
  protected readonly details$ = this.detailsSource();
  protected readonly isNotNil = isNotNil;
  /* prettier-ignore */
  protected readonly fetchMultimediaDetailsFn = (id: Guid) => this.multimediaDataSource.fetchDetailsById(id, true);

  public shouldShowCharacteristicsAccordions(
    characteristics: Nillable<AdminProductCharacteristicDetails[]>,
  ): characteristics is AdminProductCharacteristicDetails[] {
    return isNotNil(characteristics) && isNotEmptyArray(characteristics);
  }

  public shouldShowAttributesAccordions(
    attributes: Nillable<AdminProductAttributeDetails[]>,
  ): attributes is AdminProductAttributeDetails[] {
    return isNotNil(attributes) && isNotEmptyArray(attributes);
  }

  private detailsSource(): Observable<AdminProductDetails> {
    return this.id$.pipe(
      switchMap((id) => this.productDataSource.fetchDetailsById(id)),
    );
  }
}
